import { Button } from '@agms/ui'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import { useConnectGoogleCalendar, useDisconnectGoogleCalendar, useGoogleCalendarConnection } from '../hooks/useGoogleCalendarConnection'
import { SurfaceCard } from './ui'

function callbackNotice(): string | null {
  const flag = new URLSearchParams(window.location.search).get('google')
  if (flag === 'connected') {
    return 'Google Calendar was just connected.'
  }
  if (flag === 'error') {
    return 'Google Calendar could not be connected. Try again.'
  }
  return null
}

export function GoogleCalendarConnectionCard() {
  const query = useGoogleCalendarConnection()
  const connect = useConnectGoogleCalendar()
  const disconnect = useDisconnectGoogleCalendar()
  const notice = callbackNotice()
  const status = query.data
  const busy = connect.isPending || disconnect.isPending

  let body = <p className="text-sm text-slate-600">Checking Google Calendar…</p>
  if (query.isError) {
    body = <p className="text-sm text-red-700" role="alert">Google Calendar status is unavailable.</p>
  } else if (status?.reconnect_required) {
    body = (
      <div className="space-y-3">
        <p className="text-sm text-amber-800" role="alert">
          Google Calendar needs to be reconnected. Appointments in the CRM are unchanged.
        </p>
        <Button type="button" onClick={() => connect.mutate()} disabled={busy}>
          Reconnect Google Calendar
        </Button>
      </div>
    )
  } else if (status?.connected) {
    body = (
      <div className="space-y-3">
        <p className="text-sm text-slate-700">
          {status.google_email ? `Connected as ${status.google_email}` : 'Google Calendar is connected.'}
        </p>
        <Button type="button" variant="secondary" onClick={() => disconnect.mutate()} disabled={busy}>
          Disconnect Google Calendar
        </Button>
      </div>
    )
  } else if (status) {
    body = (
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Connect your Google account to put CRM appointments on your calendar.
        </p>
        <Button type="button" onClick={() => connect.mutate()} disabled={busy}>
          Connect Google Calendar
        </Button>
      </div>
    )
  }

  return (
    <SurfaceCard padding="p-4">
      <section aria-label="Google Calendar" className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-900">Google Calendar</h2>
        {notice && (
          <p className="text-sm text-slate-700" role={notice.includes('could not') ? 'alert' : 'status'}>
            {notice}
          </p>
        )}
        {body}
        {connect.isError && (
          <p className="text-sm text-red-700" role="alert">
            {apiErrorMessage(connect.error, 'Google Calendar could not be connected.')}
          </p>
        )}
        {disconnect.isError && (
          <p className="text-sm text-red-700" role="alert">
            {apiErrorMessage(disconnect.error, 'Google Calendar could not be disconnected.')}
          </p>
        )}
      </section>
    </SurfaceCard>
  )
}
